<script module>
  export const i18n = {
    sv: {
      'Too close to the departure date, try a later date.':
        'För nära i tiden, försök med ett senare datum.',
      'There was a problem connecting to the booking system for this trip. Please try again later.':
        'Det uppstod ett problem i vår koppling till bokningssystemet för denna resa. Försök igen senare.',
      'Tickets between ${origin} and ${destination} are typically purchased at the station before departure.':
        'Biljetter mellan %s och %s köps vanligtvis på stationen innan avgången.',
      'Tickets for this train between %s and %s are sold out.':
        'Biljetter för tåget mellan %s och %s är slutsålda.',
      "We're currently unable to offer a ticket for this trip. Please check with the operator directly.":
        'Vi kan för tillfället inte erbjuda biljetter för denna resa. Kontakta operatören direkt.',
      'Tickets for this trip are not available yet. Please check back closer to your travel date.':
        'Biljetterna för din resa är inte tillgängliga än. Försök igen senare.',
      "Our booking system didn't return any results for this route.":
        'Vi kunde inte hitta några biljetter för denna resa.',
      'The timetable for this route is not yet available. Please check back closer to your travel date.':
        'Tidtabellen för denna resa är inte tillgänglig än. Försök igen vid ett senare datum.',
      'towards %s': 'mot %s',
      'These tickets are typically purchased at the station before departure.':
        'Dessa biljetter köps vanligtvis på stationen innan avgången.',
      'Looking for tickets.': 'Söker efter biljetter.',
      'Spend a night in': 'En natt i',
      'Show details': 'Visa detaljer',
      'Hide details': 'Dölj detaljer',
      'Travel with a': 'Res med',
      'Night train': 'Nattåg',
      'Change train': 'Byt tåg'
    }
  }
</script>

<script>
  import { SvelteSet } from 'svelte/reactivity'
  import * as Duration from 'duration-fns'
  import { tick } from 'svelte'
  import {
    differenceInCalendarDays,
    differenceInMinutes,
    addHours
  } from 'date-fns'

  import { format } from '$lib/utils/duration.svelte.js'
  import { register } from '$lib/utils/text.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import { slide } from 'svelte/transition'

  const WEAK_CONTENT_COUNTRIES = ['PL', 'SK', 'HU', 'SI']

  let text = register(i18n)

  /** @type {{
   * itinerary?: Schema.ItineraryItem[]|null,
   * showStatus?: boolean
   * }} */
  let { itinerary, showStatus = false } = $props()

  let id = $derived(
    itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .flatMap((/** @type {Schema.SegmentCollection} */ item) =>
        item.segments?.map((segment) => segment.identifier)
      )
      .join('-')
  )

  /** @type {SvelteSet<string>} */
  let expanded = new SvelteSet()

  /** @type {string|undefined} */
  let animating = $state()

  /** @type {{ [key: string]: HTMLElement }} */
  let expandables = $state({})

  const toggleDetails = async (identifier) => {
    animating = identifier

    const element = expandables[identifier]
    const before = element.getBoundingClientRect()

    if (expanded.has(identifier)) {
      expanded.delete(identifier)
    } else {
      expanded.add(identifier)
    }

    await tick()
    const after = element.getBoundingClientRect()

    const animation = element.animate(
      [{ height: `${before.height}px` }, { height: `${after.height}px` }],
      {
        duration: 300,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    )

    await animation.finished
    animating = undefined
  }

  const asTimestamp = $derived.by(() => (date) => {
    return new Date(date).toLocaleTimeString(State.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
  })

  /** @type {function(string, string): boolean} */
  const isSameDay = (a, b) => {
    if (!a || !b) return false
    const [aDate] = a.split('T')
    const [bDate] = b.split('T')
    return aDate === bDate
  }

  /** @param {string} date */
  const isFarAhead = (date) => {
    const threeMonths = Date.now() + 1000 * 60 * 60 * 24 * 90
    return Date.parse(date) > +threeMonths
  }

  /** @param {Schema.SegmentCollection} item */
  const isWeakContentCountry = (item) => {
    const locations = item.segments?.flatMap((segment) => [
      segment.origin,
      segment.destination
    ])
    return locations?.some(
      (location) =>
        'countryCode' in location &&
        WEAK_CONTENT_COUNTRIES.includes(location.countryCode)
    )
  }

  /** @param {Schema.SegmentCollection} item */
  const getStatusMessage = (item) => {
    const { status, segments, availability } = item
    const origin = segments?.[0]?.origin.name
    const destination = segments?.[segments.length - 1]?.destination.name
    switch (status) {
      case 'ONE_DAY':
        return text`Too close to the departure date, try a later date.`
      case 'NO_TIMETABLE':
      case 'OUTSIDE_TIMETABLE':
        return text`The timetable for this route is not yet available. Please check back closer to your travel date.`
      case 'NO_FARES': {
        if (segments) {
          if (isFarAhead(segments[0].departureAt)) {
            return text`Tickets for this trip are not available yet. Please check back closer to your travel date.`
          }
          if (isWeakContentCountry(item)) {
            return text`We're currently unable to offer a ticket for this trip. Please check with the operator directly.`
          }
        }
        return text`Our booking system didn't return any results for this route.`
      }
      case 'PREBOOK_NOT_POSSIBLE': {
        if (segments) {
          return text`Tickets between ${origin} and ${destination} are typically purchased at the station before departure.`
        }
        return text`These tickets are typically purchased at the station before departure.`
      }
      case 'LOADING':
        return text`Looking for tickets.`
      case 'INDETERMINATE':
        return null
      case 'UNKNOWN':
      case 'UNABLE_TO_SELL':
        if (availability?.status === 'SOLD_OUT' && segments) {
          return text`Tickets for this train between ${origin} and ${destination} are sold out.`
        }
        if (isWeakContentCountry(item)) {
          return text`We're currently unable to offer a ticket for this trip. Please check with the operator directly.`
        }
        return text`There was a problem connecting to the booking system for this trip. Please try again later.`
      default:
        return null
    }
  }

  const getStatusSymbol = (status) => {
    switch (status) {
      case 'LOADING':
        return 'spinner'
      case 'SUCCESS':
        return 'checkmark'
      default:
        return 'question'
    }
  }

  /** @param {Schema.Stopover} stopover */
  const formatStopoverDuration = (stopover) => {
    if (!itinerary) return null

    const index = itinerary.indexOf(stopover)
    const { nights } = itinerary.slice(0, index + 1).reduce((acc, item) => {
      // Keep track of the arrival time of the previous segment
      if (item.__typename === 'SegmentCollection') {
        const lastSegment = item.segments?.[item.segments.length - 1]
        return { nights: 0, date: new Date(lastSegment?.arrivalAt) }
      }

      // Only count stopovers
      if (item.__typename !== 'Stopover') {
        return { nights: 0, date: null }
      }

      const duration = Duration.parse(item.duration)
      if (acc.date) {
        const hours = Duration.toHours(duration)
        const date = addHours(acc.date, hours)
        const diff = differenceInCalendarDays(date, acc.date)
        return { nights: diff, date }
      }

      return { nights: 0, date: null }
    }, /** @type {{ nights: number, date: Date|null }} */ ({ nights: 0, date: null }))

    return nights ? text`${nights} nights in` : text`Spend a night in`
  }

  /** @param {Schema.Segment} segment */
  const isNightTrain = (segment) => {
    return segment.transport === 'NIGHT_TRAIN' && segment.duration > 300
  }
</script>

{#snippet highlight(location, label)}
  <div class="highlight">
    <Symbol name="bed" size="md" circle />
    <div class="truncate">
      {label}
      <strong>{location.name}</strong>
    </div>
  </div>
{/snippet}

{#snippet day(
  /** @type {{
  index: number,
  segments: Schema.Segment[],
  prevItem: Schema.ItineraryItem|null
}} */ { index, segments, prevItem }
)}
  {@const segment = segments[index]}
  {@const prevSegment =
    index === 0
      ? prevItem?.__typename === 'SegmentCollection'
        ? prevItem.segments?.at(-1)
        : null
      : segments[index - 1]}
  {#if prevItem?.__typename === 'Stopover' || (prevSegment && !isSameDay(prevSegment.departureAt, segment.departureAt))}
    <div class="divider day">
      <span class="text">
        {new Date(segment.departureAt).toLocaleDateString(State.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'short'
        })}
      </span>
    </div>
  {:else if prevSegment}
    {@const duration = differenceInMinutes(
      new Date(segment.departureAt),
      new Date(prevSegment.arrivalAt)
    )}
    <div class="divider change">
      <span class="text">
        {text`Change train`} ({format({ minutes: duration })})
      </span>
    </div>
  {/if}
{/snippet}

{#snippet leg(
  /** @type {{
  segment: Schema.Segment,
  status: Schema.SegmentCollectionStatus,
  item: Schema.SegmentCollection
}} */ { segment, status, item }
)}
  {@const identifier = segment.identifier || '#'}
  {@const isExpanded = expanded.has(identifier)}
  <div class="segment">
    <div class="overview">
      <div>
        <time datetime={segment.departureAt} class="time">
          {asTimestamp(segment.departureAt)}
        </time>
        {segment.origin.name}
      </div>
      <div>
        <time datetime={segment.arrivalAt} class="time">
          {asTimestamp(segment.arrivalAt)}
          {#if !isSameDay(segment.departureAt, segment.arrivalAt)}
            {@const dayDiff = differenceInCalendarDays(
              new Date(segment.arrivalAt),
              new Date(segment.departureAt)
            )}
            <span class="daychange">+{dayDiff}</span>
          {/if}
        </time>
        {segment.destination.name}
      </div>
      <div class="duration">
        {format({ minutes: segment.duration })}
      </div>
    </div>
    <div
      class="details"
      class:open={isExpanded}
      class:animating={animating === identifier}>
      <div class="header">
        {#if itinerary && showStatus}
          {@const statusClass = status
            .replace(/^(?!SUCCESS$|LOADING$).*/, 'error')
            .toLowerCase()}
          <span
            class="status {statusClass}"
            in:slide={{ duration: 200, axis: 'x' }}>
            <span class="inner">
              {#if status === 'LOADING'}
                <span class="figure">
                  <Symbol name="spinner" size="fill" />
                </span>
              {:else}
                <span class="figure">
                  <Symbol name={getStatusSymbol(status)} size="sm" circle />
                </span>
              {/if}
            </span>
          </span>
        {/if}
        <div class="summary">
          <div class="section">
            <span class="info">
              {#if segment.operator.image}
                <img
                  class="logotype"
                  src={segment.operator.image.url}
                  alt={segment.operator.name} />
              {:else}
                {segment.operator.name}
              {/if}
            </span>
            {#if segment.serviceBrand}
              <span class="info">
                {#if segment.serviceBrand.image}
                  <img
                    class="logotype"
                    src={segment.serviceBrand.image.url}
                    alt={segment.serviceBrand.name} />
                {:else}
                  {segment.serviceBrand.name}
                {/if}
              </span>
            {/if}
          </div>
          <div class="section">
            {#if isNightTrain(segment)}
              <span class="nighttrain"> Night train </span>
            {:else}
              {segment.identifier?.replace(/^(\d)/, '#$1')}
            {/if}
          </div>
          <button
            class="toggle"
            type="button"
            onclick={(event) => {
              toggleDetails(identifier)
              event.preventDefault()
            }}
            aria-controls={`details-${id}--${identifier}`}>
            <span class="chevron">
              <Symbol name="chevron" size="fill" />
            </span>
            {isExpanded ? text`Hide details` : text`Show details`}
          </button>
        </div>
      </div>

      <div
        class="expandable"
        id={`details-${id}--${identifier}`}
        aria-expanded={isExpanded ? 'true' : 'false'}
        bind:this={expandables[identifier]}>
        <div class="body">
          <div class="top">
            <strong>
              {#if segment.identifier}
                {segment.identifier.replace(/^(\d)/, '#$1')}
                {#if segment.direction}
                  {text`towards ${segment.direction}`}
                {/if}
              {/if}
            </strong>
            <span class="date">
              {new Date(segment.departureAt).toLocaleDateString(
                State.language,
                {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                }
              )}
            </span>
          </div>
          <span class="extra">
            {#if segment.serviceBrand}
              {segment.operator.name}, {segment.serviceBrand.name}
            {:else}
              {segment.operator.name}
            {/if}
          </span>
          {#if showStatus}
            <p class="message">
              {getStatusMessage(item)}
            </p>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/snippet}

<div class="journey-details">
  {#if itinerary}
    <div class="items">
      {#each itinerary as item, index (`${item.__typename}-${index}`)}
        {@const prev = itinerary[index - 1]}
        {#if item.__typename === 'Stopover'}
          <div class="item">
            {@render highlight(item.location, formatStopoverDuration(item))}
          </div>
        {:else if item.__typename === 'SegmentCollection' && item.segments}
          {@const segments = item.segments}
          <div class="item">
            {@render day({ index: 0, segments, prevItem: prev })}
            <div class="legs">
              {#each segments as segment, index (segment.identifier || index)}
                {#if index}
                  {@render day({ index, segments, prevItem: prev })}
                {/if}
                {@render leg({ segment, status: item.status, item })}
              {/each}
            </div>
          </div>
        {/if}
      {/each}
    </div>
  {/if}
</div>

<style>
  .journey-details {
    container-type: inline-size;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    position: relative;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    background: var(--background);
  }

  @container (width >= 35rem) {
    .journey-details {
      border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
    }
  }

  /** Items */

  .items,
  .item {
    display: grid;
    gap: 1rem;
  }

  /** Legs */

  .legs {
    display: grid;
    gap: 1rem;
    position: relative;
  }

  /** Segment */

  .segment {
    display: grid;
    gap: 0.875rem;
  }

  @container (width >= 25rem) {
    .segment {
      gap: 0.75rem;
    }
  }

  /** Overview */

  .overview {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 14rem) minmax(0, 14rem) 1rem;
    gap: 0.5rem;
    font-size: 0.9375rem;
  }

  @container (width >= 25rem) {
    .overview {
      font-size: 1rem;
    }
  }

  .daychange {
    font-weight: 500;
    font-size: 0.75rem;
    position: relative;
    top: -0.25em;
  }

  .time {
    display: block;
    font-weight: bold;
    line-height: 1.1;
    height: 1.25rem;
    font-size: 1.125rem;
    letter-spacing: -0.03em;
    margin-bottom: 0.25rem;
  }

  .duration {
    position: absolute;
    font-size: 0.9375rem;
    top: 0;
    right: 0;
    color: rgb(var(--rgb-muted));
  }

  .nighttrain {
    color: rgb(var(--rgb-text));
  }

  /** Details */

  .details {
    border-radius: var(--border-radius-xs);
  }

  .header {
    width: 100%;
    position: relative;
    display: flex;
    justify-content: space-between;
  }

  .summary {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 2.375rem;
    padding: 0 0.75rem;
    gap: 1rem;
    padding-right: 2.5rem;
    flex-wrap: wrap;
    white-space: nowrap;
    font-weight: normal;
    background: rgba(var(--rgb-muted), 0.08);
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
    border-radius: var(--border-radius-xs) var(--border-radius-xs) 0 0;
    transition:
      background 100ms var(--ease-out),
      border-radius 100ms var(--ease-out);
  }

  .details:not(.open) .summary {
    border-radius: var(--border-radius-xs);
  }

  .details:not(.open) .status + .summary {
    border-radius: 0 var(--border-radius-xs) var(--border-radius-xs) 0;
  }

  .details.open .status + .summary {
    border-radius: 0 var(--border-radius-xs) 0 0;
  }

  .summary:hover {
    background: rgba(var(--rgb-muted), 0.15);
    transition: background 100ms var(--ease-in);
  }

  .summary .section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logotype {
    height: 1.125rem;
    width: auto;
  }

  .status {
    flex: 0 0 auto;
    display: flex;
    border-right: 2px solid var(--background);
    background: var(--background);
  }

  .status .inner {
    --current-color: rgb(var(--rgb-text));
    --current-background: var(--background-alt);

    padding: 0 0.625rem;
    display: flex;
    align-items: center;
    background: var(--background-alt);
    border-radius: var(--border-radius-xs) 0 0 var(--border-radius-xs);
    transition:
      background 150ms var(--ease),
      border-radius 150ms var(--ease);
  }

  .details.open .status .inner {
    border-radius: var(--border-radius-xs) 0 0 0;
  }

  .status .figure {
    width: 1rem;
    height: 1rem;
  }

  .status.error .inner {
    --current-color: rgb(var(--rgb-white));
    --current-background: #e4ab00;

    background: rgba(var(--rgb-yellow), 0.25);
  }

  .status.success .inner {
    --current-color: rgb(var(--rgb-white));
    --current-background: rgb(var(--rgb-bright-green));

    background: rgb(var(--rgb-bright-green), 0.25);
  }

  .toggle {
    font-size: 0;
    position: absolute;
    inset: 0;
  }

  .chevron {
    height: 1.625rem;
    transform-origin: center;
    display: flex;
    width: fit-content;
    margin-right: 0.5rem;
    margin-left: auto;
  }

  .details.open .chevron {
    transform: rotate(180deg);
  }

  .expandable {
    overflow: hidden;
  }

  .details:not(.open) .expandable {
    height: 0;
  }

  .body {
    width: 100%;
    padding: 0.75rem;
    font-size: 0.9375rem;
    border-top: 2px solid var(--background);
    background: rgba(var(--rgb-muted), 0.08);
    border-radius: 0 0 var(--border-radius-xs) var(--border-radius-xs);
  }

  .top {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.125rem;
  }

  .body :is(.date, .extra) {
    color: rgb(var(--rgb-muted));
  }

  @container (width <= 25rem) {
    .date {
      display: none;
    }
  }

  .message {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  .message:empty {
    display: none;
  }

  /** Leg dividers */

  .divider {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 0.25rem;
    font-size: 0.875rem;
    margin: 0 -1rem;
  }

  .divider::before {
    content: '';
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    transform: translateY(-50%);
  }

  .divider .text {
    position: relative;
    color: rgb(var(--rgb-muted));
    background: var(--background);
    padding: 0 0.5rem;
  }

  /** Stopovers */

  .highlight {
    --current-background: rgb(var(--rgb-muted));
    --current-color: rgb(var(--rgb-white));

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
  }
</style>
